import React, { useEffect, useMemo } from 'react';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import PropTypes from 'prop-types';
import { DriverItem } from '../../../layout/DriverItem';
import { AccordionSkeleton } from '../../../layout/Accordion';
import { usePaginatedDriversHistory } from '../OrderHistoryPage.hooks';
import { historyAccessMessage } from '../../../../services/HistoryErrors';
import DriverHistoryDetails from './DriverHistoryDetails';

export function _DriverSortedList({
  type = 'default',
  dateRange,
  setMetadata,
  traversal,
}) {
  const { drivers, isFetching, isError, error, refetch, historyChanged, page, setPage, traversal: report } = usePaginatedDriversHistory({
    dateRange,
    traversal,
  });
  const results = drivers?.results;
  const totalPages = drivers?.totalPages;
  const accessMessage = historyAccessMessage(error);

  useEffect(() => {
    setMetadata({
      totalResults: isFetching || isError ? null : drivers?.totalResults,
      totalEarned: isFetching || isError ? null : drivers?.totalEarned,
      earningsStatus: isFetching || isError ? undefined : drivers?.earningsStatus,
      completedOrders: isFetching || isError ? null : drivers?.completedOrders,
      missingCompensationCount: isFetching || isError ? null : drivers?.missingCompensationCount,
      rangeKey: JSON.stringify(dateRange),
      sortBy: 'driver',
    });
  }, [drivers, setMetadata, isFetching, isError, dateRange]);

  const Content = useMemo(() => {
    if (isError) {
      return (
        <Box alignItems="center" justifyContent="center" display="flex" p={2}>
          <Typography role="alert">{accessMessage || (historyChanged ? 'History changed. Restart from page 1 to see current totals.' : 'We had a problem loading the requested items')}</Typography>
          {!accessMessage && <Button
            data-test-id="nbnJE3qUg_6c_seeBvGnD"
            padding={2}
            onClick={refetch}
          >
            Retry
          </Button>}
        </Box>
      );
    }
    if (isFetching) {
      return Array(10)
        .fill()
        .map((_, i) => <AccordionSkeleton key={i} />);
    }
    if (results) {
      if (results.length === 0) {
        return (
          <Box p={1}>
            <Typography>No history</Typography>
          </Box>
        );
      }
      return results?.map(({ id, firstName, lastName, deliveries, earned, earningsStatus, missingCompensationOrderIds, missingCompensationCount }) => (
        <Box
          data-testid="driver-list:accordion"
          key={id}
          component={Paper}
          variant="outlined"
          mb={1}
          px={2}
          py={0.5}
          borderRadius={8}
          minWidth={0}
        >
          <DriverItem
            key={id}
            type={type}
            firstName={firstName}
            lastName={lastName}
            active={deliveries?.active}
            orders={deliveries?.done}
            earned={earned}
            earningsStatus={earningsStatus}
            missingCompensationOrderIds={missingCompensationOrderIds}
            missingCompensationCount={missingCompensationCount}
            hideAvatar
            hideAction
            hideRipple
            disableGutters
          />
          {report && <DriverHistoryDetails key={`${id}-${drivers.snapshot}`} driverId={id} report={report} />}
        </Box>
      ));
    }
    return null;
  }, [isFetching, isError, results, refetch, type, historyChanged, accessMessage, report, drivers]);

  return (
    <>
      {results?.some((driver) => driver.earningsStatus === 'error') && !isFetching && !isError && (
        <Box role="alert" p={1}>
          Some driver totals could not be loaded.
          <Button onClick={refetch}>Retry earnings</Button>
        </Box>
      )}
      <Box display="flex" justifyContent="center" paddingY={2}>
        <Pagination
          count={totalPages || 0}
          disabled={isFetching || isError}
          page={page}
          onChange={(_, value) => {
            setPage(value);
          }}
        />
      </Box>
      {Content}
    </>
  );
}

_DriverSortedList.propTypes = {
  type: PropTypes.oneOf(['default', 'order-history']),
  setMetadata: PropTypes.func,
  dateRange: PropTypes.arrayOf(PropTypes.any),
};

export const DriverSortedList = React.memo(_DriverSortedList);
